import { ConsolidationGroupResponse } from "@common/schemas/domains/accounting/consolidation.schema";
import { ConsolidationGroupStatus } from "@common/types/domains/accounting/consolidation";
import { Entity } from "@common/types/entity";
import { useConsolidationGroups } from "@api/consolidation.api";
import { useEntities } from "@hooks/useEntities";
import { useScope } from "@state/scopeContext.lib";

export interface JournalEntryConsolidationContext {
    workspaceId: string;
    group: ConsolidationGroupResponse;
    parentEntity: Entity;
    displayName: string;
}

export type JournalEntryConsolidationContextResult =
    | { status: "loading" }
    | { status: "absent" }
    | { status: "ready"; context: JournalEntryConsolidationContext };

interface ResolveActiveConsolidationContextParams {
    workspaceId: string | null | undefined;
    groups: ConsolidationGroupResponse[] | undefined;
    entities: readonly Entity[];
    entityUuid?: string;
}

function isActiveGroup(group: ConsolidationGroupResponse): boolean {
    return group.status === ConsolidationGroupStatus.active;
}

function groupContainsEntity(
    group: ConsolidationGroupResponse,
    entityUuid: string,
): boolean {
    return (
        group.parentEntityUuid === entityUuid ||
        group.members.some((member) => member.entityUuid === entityUuid)
    );
}

export function resolveActiveConsolidationContext({
    workspaceId,
    groups,
    entities,
    entityUuid,
}: ResolveActiveConsolidationContextParams): JournalEntryConsolidationContext | null {
    if (!workspaceId || !groups || groups.length === 0 || !entityUuid) {
        return null;
    }

    const group = groups
        .filter(isActiveGroup)
        .find((activeGroup) => groupContainsEntity(activeGroup, entityUuid));
    if (!group) {
        return null;
    }

    const parentEntity = entities.find(
        (entity) => entity.uuid === group.parentEntityUuid,
    );
    if (!parentEntity) {
        return null;
    }

    return {
        workspaceId,
        group,
        parentEntity,
        displayName: `${parentEntity.name ?? "Group"} (Consolidated)`,
    };
}

export function useJournalEntryConsolidationContext(params?: {
    entityId?: number;
    entityUuid?: string;
}): JournalEntryConsolidationContextResult {
    const { workspaceId } = useScope();
    const { data: groups, isLoading: isLoadingGroups } = useConsolidationGroups(
        workspaceId ?? null,
    );
    const entities = useEntities();
    const hasGroups = (groups?.length ?? 0) > 0;
    const overlayEntityUuid =
        params?.entityUuid ??
        entities.find((entity) => entity.id === params?.entityId)?.uuid;
    const isResolvingOverlayEntity =
        params?.entityId !== undefined && entities.length === 0;

    if (isLoadingGroups || (hasGroups && isResolvingOverlayEntity)) {
        return { status: "loading" };
    }

    const context = resolveActiveConsolidationContext({
        workspaceId,
        groups,
        entities,
        entityUuid: overlayEntityUuid,
    });

    return context ? { status: "ready", context } : { status: "absent" };
}
